import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import myUnreadMessageCountQuery from '../graphql/query/myUnreadMessageCount.query';
import MessageIcon from '@mui/icons-material/Message';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import Badge from '@mui/material/Badge';
import MessagesDropdown from './Header/MessagesDropdown';
import { useCurrentUser } from '../utils';
import { useDarkMode } from '../App';
import './FloatingMessagesButton.css';

const FloatingMessagesButton: React.FC = () => {
  const currentUser = useCurrentUser();
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: unreadData } = useQuery(myUnreadMessageCountQuery, {
    skip: !currentUser?.id,
    pollInterval: 12000,
    fetchPolicy: 'network-only',
  });

  const unreadCount = unreadData?.myUnreadMessageCount ?? 0;

  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        panelRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    const timeoutId = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen((prev) => !prev);
  };

  if (!currentUser?.id) {
    return null;
  }

  // Full inbox page already covers messaging — avoid a redundant FAB.
  if (location.pathname.startsWith('/messages')) {
    return null;
  }

  // Product detail shows a sticky mobile action bar; lift the FAB above it.
  const aboveStickyBar = location.pathname.startsWith('/product/');
  const positionClass = aboveStickyBar ? 'above-sticky-bar' : '';

  return (
    <>
      <div className={`floating-messages-container ${positionClass}`.trim()}>
        <button
          ref={buttonRef}
          className={`floating-messages-button ${darkMode ? 'dark' : ''} ${isOpen ? 'open' : ''}`.trim()}
          onClick={handleToggle}
          aria-label={unreadCount > 0 ? `Messages, ${unreadCount} unread` : 'Messages'}
          aria-expanded={isOpen}
          data-testid="header-messages"
          type="button"
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '11px',
                height: '18px',
                minWidth: '18px',
              },
            }}
          >
            <MessageIcon />
          </Badge>
        </button>
      </div>

      {isOpen && (
        <div
          ref={panelRef}
          className={`floating-messages-panel ${darkMode ? 'dark' : ''} ${positionClass}`.trim()}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Messages"
          data-testid="messages-panel"
        >
          <div className="floating-messages-panel-toolbar">
            <button
              type="button"
              className="floating-messages-expand"
              onClick={() => {
                handleClose();
                navigate('/messages');
              }}
              aria-label="Open full messages page"
              title="Open full inbox"
            >
              <OpenInFullIcon fontSize="small" />
              <span>Full inbox</span>
            </button>
          </div>
          <div className="floating-messages-panel-body">
            <MessagesDropdown
              userId={currentUser.id}
              darkMode={darkMode}
              onClose={handleClose}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingMessagesButton;
